// FundPicker.tsx — fund <select>. Forecast mode allows "Eigene Parameter" (no fund,
// isin = null); backtest always needs a real fund since it reads cached market data.
import { useId } from 'preact/hooks';
import type { FundInfo } from '../lib/types.js';

export interface FundPickerProps {
  funds: FundInfo[];
  value: string | null;
  onChange: (isin: string | null) => void;
  allowCustom: boolean;
  loading?: boolean | undefined;
  error?: string | null | undefined;
}

export function FundPicker({ funds, value, onChange, allowCustom, loading, error }: FundPickerProps) {
  const id = useId();

  if (loading) {
    return (
      <div class="field">
        <label>Fonds</label>
        <div class="faint">
          <span class="spinner" /> Fonds werden geladen…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="field">
        <label>Fonds</label>
        <div class="banner banner-error" role="alert">
          <strong>Fonds konnten nicht geladen werden</strong>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  // Deaktivierte Fonds (Daten view) are hidden from the pickable list — but a scenario
  // that already references one (disabled after the fact, or deleted entirely) must never
  // silently vanish or crash: it's kept as a single disabled <option> so the selection
  // stays visible and honestly marked "unavailable" until the user picks something else.
  const enabledFunds = funds.filter((f) => f.enabled);
  const selectedFund = value ? (funds.find((f) => f.isin === value) ?? null) : null;
  const selectedIsUnavailable = value != null && (!selectedFund || !selectedFund.enabled);

  if (enabledFunds.length === 0 && !allowCustom && !selectedIsUnavailable) {
    return (
      <div class="field">
        <label>Fonds</label>
        <div class="empty-state">
          Noch keine Fonds hinterlegt — im Daten-Tab aktualisieren oder einen Fonds anlegen.
        </div>
      </div>
    );
  }

  return (
    <div class="field">
      <label for={id}>Fonds</label>
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => {
          const v = (e.currentTarget as HTMLSelectElement).value;
          onChange(v === '' ? null : v);
        }}
      >
        {allowCustom ? <option value="">Eigene Parameter</option> : null}
        {enabledFunds.map((f) => (
          <option key={f.isin} value={f.isin}>
            {f.name} ({f.isin})
          </option>
        ))}
        {selectedIsUnavailable ? (
          <option value={value ?? ''} disabled>
            {selectedFund ? `${selectedFund.name} (${selectedFund.isin}) — deaktiviert` : `${value} — nicht mehr verfügbar`}
          </option>
        ) : null}
      </select>
      {selectedIsUnavailable ? (
        <p class="field-hint">
          {selectedFund
            ? 'Dieser Fonds ist deaktiviert und daher ausgeblendet — bitte einen anderen Fonds wählen oder ihn im Daten-Tab wieder aktivieren.'
            : 'Dieser Fonds ist nicht mehr verfügbar (gelöscht) — bitte einen anderen Fonds wählen.'}
        </p>
      ) : null}
    </div>
  );
}
